import React, { useEffect, useState, useRef } from 'react';
import { logger } from '../../utils/logger';
import ReactDOM from 'react-dom';
import { PlusIcon, SearchIcon, CalendarIcon, ListIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, RefreshCwIcon, CheckIcon, DownloadIcon, ClockIcon, LockIcon, UserIcon, EditIcon, EyeIcon, StethoscopeIcon, FileTextIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Appointment } from '../../types';
import { formatTime, getToday, getTomorrow, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getDayOfWeekKey, getDayOfWeekName, getDoctorPrefix, formatCurrency } from '../../utils/helpers';
import { validateAppointmentForm } from '../../utils/validation';
import { exportAppointmentsToExcel } from '../../utils/excelExport';
import { exportAppointmentsToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { emailNotificationService } from '../../utils/emailNotifications';
import { sendDesktopNotification } from '../../utils/desktopNotifications';
import { useRolePermissions } from '../../hooks/useRolePermissions';
import { FormSection } from '../ui/FormSection';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
interface AppointmentsProps {
  initialDateFilter?: string;
  autoOpenNew?: boolean;
}
export function Appointments({
  initialDateFilter,
  autoOpenNew
}: AppointmentsProps) {
  const {
    appointments,
    addAppointment,
    updateAppointment,
    cancelAppointment,
    rescheduleAppointment,
    completeAppointment,
    patients,
    doctors,
    services,
    specialties,
    getPatient,
    getDoctor,
    getService,
    getSpecialty,
    getDoctorsBySpecialty,
    checkDoctorAvailability,
    getAppointmentsByDate,
    addAuditLog
  } = useData();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    addNotification
  } = useNotifications();
  const {
    isAdmin,
    canExport,
    getPermissionLevel
  } = useRolePermissions();
  const canExportExcel = canExport('appointments');
  const canExportPdf = canExport('appointments');
  const appointmentPermissionLevel = getPermissionLevel('appointments');
  const isOwnOnly = appointmentPermissionLevel === 'own';
  const isReadOnlyAppointments = appointmentPermissionLevel === 'read';
  const canCreateAppointments = appointmentPermissionLevel === 'full';
  const linkedDoctor = isOwnOnly && user ? doctors.find(d => !!d.email && !!user.email && d.email.toLowerCase().trim() === user.email.toLowerCase().trim()) : null;
  const hasOwnButNoDoctor = isOwnOnly && !linkedDoctor;
  const [activeView, setActiveView] = useState<'list' | 'calendar'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState(initialDateFilter || '');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const pickerBtnRef = useRef<HTMLButtonElement>(null);
  const [pickerNavDate, setPickerNavDate] = useState(new Date());
  useEffect(() => {
    if (!isDatePickerOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [isCompleteOpen, setIsCompleteOpen] = useState(false);
  const [isActionSelectOpen, setIsActionSelectOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [formData, setFormData] = useState({
    patientId: '',
    doctorId: '',
    serviceIds: [] as string[],
    date: getToday(),
    time: '09:00',
    notes: ''
  });
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [overrideRestrictions, setOverrideRestrictions] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleData, setRescheduleData] = useState({
    date: '',
    time: ''
  });
  const [completeData, setCompleteData] = useState({
    nextAppointmentDate: '',
    nextAppointmentTime: '',
    nextAppointmentNotes: '',
    nextServiceIds: [] as string[]
  });
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState('');
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [specialtyStatuses, setSpecialtyStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [serviceStatuses, setServiceStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    const loadStatuses = () => {
      try {
        const ds = localStorage.getItem('savidna-doctors-status');
        if (ds) setDoctorStatuses(JSON.parse(ds));
        const ss = localStorage.getItem('savidna-specialties-status');
        if (ss) setSpecialtyStatuses(JSON.parse(ss));
        const svs = localStorage.getItem('savidna-services-status');
        if (svs) setServiceStatuses(JSON.parse(svs));
        const ps = localStorage.getItem('savidna-patients-status');
        if (ps) setPatientStatuses(JSON.parse(ps));
      } catch {}
    };
    loadStatuses();
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-specialties-status' && e.newValue) {
        try {
          setSpecialtyStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-services-status' && e.newValue) {
        try {
          setServiceStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-patients-status' && e.newValue) {
        try {
          setPatientStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  const isSpecialtyActive = (id: string) => !specialtyStatuses[id];
  const isServiceActive = (id: string) => !serviceStatuses[id];
  const isPatientActive = (id: string) => !patientStatuses[id];
  const today = getToday();
  const filteredAppointments = appointments.filter(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const matchesSearch = !searchTerm || patient?.name.toLowerCase().includes(searchTerm.toLowerCase()) || doctor?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !filterStatus || apt.status === filterStatus;
    const matchesDate = !filterDate || apt.date === filterDate;
    const matchesOwnership = isOwnOnly && linkedDoctor ? apt.doctorId === linkedDoctor.id : true;
    return matchesSearch && matchesStatus && matchesDate && matchesOwnership;
  });
  const {
    sortedItems: sortedAppointments,
    requestSort: aptRequestSort,
    isSorted: aptIsSorted
  } = useSortableData(filteredAppointments);
  const {
    paginatedItems: paginatedAppointments,
    currentPage: aptPage,
    totalPages: aptTotalPages,
    totalItems: aptTotalItems,
    startIndex: aptStartIndex,
    endIndex: aptEndIndex,
    goToPage: aptGoToPage,
    nextPage: aptNextPage,
    prevPage: aptPrevPage
  } = usePagination(sortedAppointments);
  const statusOptions = [{
    value: '',
    label: 'Todos los estados'
  }, {
    value: 'programada',
    label: 'Programada'
  }, {
    value: 'completada',
    label: 'Completada'
  }, {
    value: 'cancelada',
    label: 'Cancelada'
  }, {
    value: 'reprogramada',
    label: 'Reprogramada'
  }];
  const patientOptions = patients.filter(p => isPatientActive(p.id)).map(p => ({
    value: p.id,
    label: p.name
  }));
  const specialtyOptions = [{
    value: '',
    label: 'Todas las especialidades'
  }, ...specialties.filter(s => {
    if (!isSpecialtyActive(s.id)) return false;
    if (formData.doctorId) {
      const selectedDoctor = doctors.find(d => d.id === formData.doctorId);
      return selectedDoctor ? s.id === selectedDoctor.specialtyId : true;
    }
    return true;
  }).map(s => ({
    value: s.id,
    label: s.name
  }))];
  const doctorOptions = selectedSpecialty ? getDoctorsBySpecialty(selectedSpecialty).filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  })) : doctors.filter(d => isDoctorActive(d.id)).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  }));
  const availableServices = services.filter(s => isServiceActive(s.id));
  const filteredFormServices = availableServices.filter(s => serviceSearchTerm ? s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) : true);
  const toggleService = (serviceId: string) => {
    setFormData(prev => {
      const currentIds = prev.serviceIds || [];
      const newIds = currentIds.includes(serviceId) ? currentIds.filter(id => id !== serviceId) : [...currentIds, serviceId];
      return {
        ...prev,
        serviceIds: newIds
      };
    });
  };
  const selectedServicesList = availableServices.filter(s => formData.serviceIds.includes(s.id));
  const totalDuration = selectedServicesList.reduce((sum, s) => sum + s.duration, 0);
  const totalPrice = selectedServicesList.reduce((sum, s) => sum + s.price, 0);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  const getDateString = (day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      const matchesDate = a.date === dateStr && a.status !== 'cancelada';
      const matchesOwnership = isOwnOnly && linkedDoctor ? a.doctorId === linkedDoctor.id : true;
      return matchesDate && matchesOwnership;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const selectedDateAppointments = getAppointmentsByDate(selectedDate);
  const openNewForm = () => {
    setSelectedAppointment(null);
    setFormData({
      patientId: '',
      doctorId: isOwnOnly && linkedDoctor ? linkedDoctor.id : '',
      serviceIds: [],
      date: getToday(),
      time: '09:00',
      notes: ''
    });
    setSelectedSpecialty('');
    setFormErrors({});
    setOverrideRestrictions(false);
    setIsFormOpen(true);
  };
  useEffect(() => {
    if (autoOpenNew) {
      const timer = setTimeout(() => openNewForm(), 50);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const openEditModal = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    const serviceIds = appointment.serviceIds && appointment.serviceIds.length > 0 ? appointment.serviceIds : [appointment.serviceId];
    setFormData({
      patientId: appointment.patientId,
      doctorId: appointment.doctorId,
      serviceIds,
      date: appointment.date,
      time: appointment.time,
      notes: appointment.notes || ''
    });
    const doctor = getDoctor(appointment.doctorId);
    if (doctor) setSelectedSpecialty(doctor.specialtyId);
    setFormErrors({});
    setOverrideRestrictions(false);
    setIsEditOpen(true);
  };
  const openCancelModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCancelReason('');
    setIsCancelOpen(true);
  };
  const openRescheduleModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setRescheduleData({
      date: appointment.date,
      time: appointment.time
    });
    setOverrideRestrictions(false);
    setIsRescheduleOpen(true);
  };
  const openCompleteModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    const currentServiceIds = appointment.serviceIds && appointment.serviceIds.length > 0 ? appointment.serviceIds : [appointment.serviceId].filter(Boolean);
    setCompleteData({
      nextAppointmentDate: '',
      nextAppointmentTime: appointment.time || '09:00',
      nextAppointmentNotes: '',
      nextServiceIds: [...currentServiceIds]
    });
    setServiceSearchTerm('');
    setIsCompleteOpen(true);
  };
  const openActionSelectModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsActionSelectOpen(true);
  };
  const openDetailModal = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsDetailOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateAppointmentForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const selectedDoctor = getDoctor(formData.doctorId);
    if (!overrideRestrictions && selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    if (!overrideRestrictions && !checkDoctorAvailability(formData.doctorId, formData.date, formData.time)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario o dentro del margen de 30 minutos entre citas'
      });
      return;
    }
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
    const serviceNames = selectedServices.map(s => s.name).join(', ');
    const duration = selectedServices.reduce((sum, s) => sum + s.duration, 0) || 30;
    addAppointment({
      ...formData,
      serviceId: formData.serviceIds[0],
      duration,
      status: 'programada'
    });
    addNotification({
      type: 'appointment_created',
      title: 'Nueva Cita Creada',
      message: `Cita para ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} el ${formData.date} a las ${formatTime(formData.time)} — ${serviceNames}`,
      relatedId: formData.patientId
    });
    sendDesktopNotification({
      title: '🗓️ Nueva Cita Creada',
      body: `${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'}\n${formData.date} a las ${formatTime(formData.time)}\n${serviceNames}`,
      tag: `appointment-created-${formData.patientId}-${formData.date}-${formData.time}`
    });
    if (patient && doctor && selectedServices.length > 0) {
      emailNotificationService.sendAppointmentCreated({
        patientName: patient.name,
        patientEmail: patient.email,
        doctorName: doctor.name,
        serviceName: serviceNames,
        date: formData.date,
        time: formData.time,
        duration,
        notes: formData.notes
      }).then(result => {
        if (result.success) {
          addNotification({
            type: 'appointment_created',
            title: '📧 Correo Enviado',
            message: `Notificación enviada a ${patient.email}`,
            relatedId: formData.patientId
          });
        } else if (emailNotificationService.isEnabled()) {
          addNotification({
            type: 'appointment_cancelled',
            title: '⚠️ Error de Correo',
            message: `No se pudo enviar correo a ${patient.email}: ${result.error || 'Error desconocido'}`,
            relatedId: formData.patientId
          });
        }
      }).catch(err => {
        logger.error('Error enviando email de cita creada:', err);
        if (emailNotificationService.isEnabled()) {
          addNotification({
            type: 'appointment_cancelled',
            title: '⚠️ Error de Correo',
            message: `No se pudo enviar correo a ${patient.email}`,
            relatedId: formData.patientId
          });
        }
      });
    }
    setIsFormOpen(false);
  };
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAppointment) return;
    const validation = validateAppointmentForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const selectedDoctor = getDoctor(formData.doctorId);
    if (!overrideRestrictions && selectedDoctor && selectedDoctor.schedule) {
      const dayKey = getDayOfWeekKey(formData.date) as keyof typeof selectedDoctor.schedule;
      const daySchedule = selectedDoctor.schedule[dayKey];
      const dayName = getDayOfWeekName(formData.date);
      const prefix = getDoctorPrefix(selectedDoctor.gender, getSpecialty(selectedDoctor.specialtyId)?.name);
      if (!daySchedule) {
        setFormErrors({
          date: `${prefix} ${selectedDoctor.name} no trabaja los ${dayName}`
        });
        return;
      }
      const requestedMinutes = parseInt(formData.time.split(':')[0]) * 60 + parseInt(formData.time.split(':')[1]);
      const startMinutes = parseInt(daySchedule.start.split(':')[0]) * 60 + parseInt(daySchedule.start.split(':')[1]);
      const endMinutes = parseInt(daySchedule.end.split(':')[0]) * 60 + parseInt(daySchedule.end.split(':')[1]);
      if (requestedMinutes < startMinutes || requestedMinutes >= endMinutes) {
        setFormErrors({
          time: `Fuera del horario. ${dayName}: ${formatTime(daySchedule.start)} - ${formatTime(daySchedule.end)}`
        });
        return;
      }
    }
    if (!overrideRestrictions && !checkDoctorAvailability(formData.doctorId, formData.date, formData.time, editingAppointment.id)) {
      setFormErrors({
        time: 'El doctor ya tiene una cita en este horario o dentro del margen de 30 minutos entre citas'
      });
      return;
    }
    const patient = getPatient(formData.patientId);
    const doctor = getDoctor(formData.doctorId);
    const selectedServices = services.filter(s => formData.serviceIds.includes(s.id));
    const serviceNames = selectedServices.map(s => s.name).join(', ');
    const duration = selectedServices.reduce((sum, s) => sum + s.duration, 0) || 30;
    await updateAppointment(editingAppointment.id, {
      patientId: formData.patientId,
      doctorId: formData.doctorId,
      serviceId: formData.serviceIds[0],
      serviceIds: formData.serviceIds,
      date: formData.date,
      time: formData.time,
      duration,
      notes: formData.notes
    });
    addAuditLog({
      userId: user?.id || 'system',
      userName: user?.name || 'Sistema',
      action: 'update',
      module: 'appointments',
      entityId: editingAppointment.id,
      entityName: patient?.name || 'Paciente',
      details: `Cita editada: ${patient?.name || 'Paciente'} con ${doctor?.name || 'Doctor'} — ${formData.date} ${formData.time} — Servicios: ${serviceNames}`
    });
    addNotification({
      type: 'appointment_rescheduled',
      title: 'Cita Actualizada',
      message: `La cita de ${patient?.name || 'Paciente'} ha sido actualizada.`,
      relatedId: editingAppointment.id
    });
    if (patient && doctor && selectedServices.length > 0) {
      emailNotificationService.sendAppointmentRescheduled({
        patientName: patient.name,
        patientEmail: patient.email,
        doctorName: doctor.name,
        serviceName: serviceNames,
        date: formData.date,
        time: formData.time,
        duration,
        oldDate: editingAppointment.date,
        oldTime: editingAppointment.time
      }).catch(err => logger.error('Error enviando email de cita actualizada:', err));
    }
    setIsEditOpen(false);
    setEditingAppointment(null);
  };
  const handleCancel = () => {
    if (selectedAppointment) {
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      cancelAppointment(selectedAppointment.id, cancelReason);
      addNotification({
        type: 'appointment_cancelled',
        title: 'Cita Cancelada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} del ${selectedAppointment.date} fue cancelada. Motivo: ${cancelReason || 'No especificado'}`,
        relatedId: selectedAppointment.id
      });
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentCancelled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: selectedAppointment.date,
          time: selectedAppointment.time,
          duration: selectedAppointment.duration,
          cancelReason
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de cancelación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          logger.error('Error enviando email de cita cancelada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de cancelación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsCancelOpen(false);
    }
  };
  const handleReschedule = () => {
    if (selectedAppointment) {
      if (!overrideRestrictions && !checkDoctorAvailability(selectedAppointment.doctorId, rescheduleData.date, rescheduleData.time, selectedAppointment.id)) {
        showToast({
          type: 'warning',
          title: 'Horario no disponible',
          message: 'El doctor no está disponible en este horario. Recuerda que hay un margen de 30 minutos entre citas.'
        });
        return;
      }
      const patient = getPatient(selectedAppointment.patientId);
      const doctor = getDoctor(selectedAppointment.doctorId);
      const service = getService(selectedAppointment.serviceId);
      rescheduleAppointment(selectedAppointment.id, rescheduleData.date, rescheduleData.time);
      addNotification({
        type: 'appointment_rescheduled',
        title: 'Cita Reprogramada',
        message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} fue reprogramada de ${selectedAppointment.date} ${formatTime(selectedAppointment.time)} a ${rescheduleData.date} ${formatTime(rescheduleData.time)}`,
        relatedId: selectedAppointment.id
      });
      if (patient && doctor && service) {
        emailNotificationService.sendAppointmentRescheduled({
          patientName: patient.name,
          patientEmail: patient.email,
          doctorName: doctor.name,
          serviceName: service.name,
          date: rescheduleData.date,
          time: rescheduleData.time,
          duration: selectedAppointment.duration,
          oldDate: selectedAppointment.date,
          oldTime: selectedAppointment.time
        }).then(result => {
          if (result.success) {
            addNotification({
              type: 'appointment_created',
              title: '📧 Correo Enviado',
              message: `Notificación de reprogramación enviada a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          } else if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de reprogramación a ${patient.email}: ${result.error || 'Error desconocido'}`,
              relatedId: selectedAppointment.id
            });
          }
        }).catch(err => {
          logger.error('Error enviando email de cita reprogramada:', err);
          if (emailNotificationService.isEnabled()) {
            addNotification({
              type: 'appointment_cancelled',
              title: '⚠️ Error de Correo',
              message: `No se pudo enviar correo de reprogramación a ${patient.email}`,
              relatedId: selectedAppointment.id
            });
          }
        });
      }
      setIsRescheduleOpen(false);
    }
  };
  const handleComplete = async () => {
    if (selectedAppointment) {
      try {
        const patient = getPatient(selectedAppointment.patientId);
        const doctor = getDoctor(selectedAppointment.doctorId);
        const serviceIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
        const selectedServices = services.filter(s => serviceIds.includes(s.id));
        const serviceNames = selectedServices.map(s => s.name).join(', ');
        await completeAppointment(selectedAppointment.id, completeData.nextAppointmentDate, completeData.nextAppointmentNotes);
        if (completeData.nextAppointmentDate && completeData.nextServiceIds.length > 0) {
          setTimeout(() => {
            const followUpApts = appointments.filter(a => a.patientId === selectedAppointment.patientId && a.date === completeData.nextAppointmentDate && a.status === 'programada');
            const followUp = followUpApts[followUpApts.length - 1];
            if (followUp) {
              const nextServices = services.filter(s => completeData.nextServiceIds.includes(s.id));
              const nextDuration = nextServices.reduce((sum, s) => sum + s.duration, 0) || 30;
              updateAppointment(followUp.id, {
                serviceId: completeData.nextServiceIds[0],
                serviceIds: completeData.nextServiceIds,
                time: completeData.nextAppointmentTime || followUp.time,
                duration: nextDuration
              });
            }
          }, 500);
        }
        const nextInfo = completeData.nextAppointmentDate ? ` — Próxima cita: ${completeData.nextAppointmentDate}` : '';
        addNotification({
          type: 'appointment_created',
          title: 'Cita Completada',
          message: `La cita de ${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'} (${serviceNames}) fue completada exitosamente.${nextInfo}`,
          relatedId: selectedAppointment.id
        });
        sendDesktopNotification({
          title: '✅ Cita Completada',
          body: `${patient?.name || 'Paciente'} con Dr. ${doctor?.name || 'Doctor'}\n${serviceNames}${nextInfo}`,
          tag: `appointment-completed-${selectedAppointment.id}`
        });
        setIsCompleteOpen(false);
      } catch (error) {
        logger.error('Error completando cita:', error);
        showToast({
          type: 'error',
          title: 'Error',
          message: 'Error al completar la cita. Intente nuevamente.'
        });
      }
    }
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = appointments;
    if (dateFrom && dateTo) {
      dataToExport = appointments.filter(a => a.date >= dateFrom && a.date <= dateTo);
    }
    exportAppointmentsToExcel(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-');
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = appointments;
    if (dateFrom && dateTo) {
      dataToExport = appointments.filter(a => a.date >= dateFrom && a.date <= dateTo);
    }
    exportAppointmentsToPdf(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-', (doctorId: string) => getDoctor(doctorId)?.name || '-', (serviceId: string) => getService(serviceId)?.name || '-');
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const viewTabs = [{
    id: 'list',
    label: 'Lista',
    icon: <ListIcon className="w-4 h-4" data-id="element-220" />
  }, {
    id: 'calendar',
    label: 'Calendario',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-221" />
  }];
  const renderAppointmentForm = (isEdit = false) => <div className="space-y-6" data-id="element-222">
      <FormSection title="Paciente" data-id="element-223">
        <Select label="Seleccionar Paciente" options={patientOptions} value={formData.patientId} onChange={value => setFormData({
        ...formData,
        patientId: value
      })} error={formErrors.patientId} searchable placeholder="Buscar paciente..." data-id="element-224" />
      </FormSection>
      <FormSection title="Doctor" data-id="element-225">
        {isOwnOnly && linkedDoctor && !isEdit ? <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4" data-id="element-226">
            <p className="text-sm text-teal-700 dark:text-teal-400 font-medium" data-id="element-227">Doctor asignado automáticamente</p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1" data-id="element-228">{getDoctorPrefix(linkedDoctor.gender, getSpecialty(linkedDoctor.specialtyId)?.name)} {linkedDoctor.name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-229">{getSpecialty(linkedDoctor.specialtyId)?.name || ''}</p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-230">
            <Select label="Especialidad (opcional)" options={specialtyOptions} value={selectedSpecialty} onChange={value => {
          setSelectedSpecialty(value);
          setFormData({
            ...formData,
            doctorId: ''
          });
        }} data-id="element-231" />
            <Select label="Doctor" options={doctorOptions} value={formData.doctorId} onChange={value => {
          if (value) {
            const selectedDoc = doctors.find(d => d.id === value);
            if (selectedDoc) setSelectedSpecialty(selectedDoc.specialtyId);
          }
          setFormData({
            ...formData,
            doctorId: value
          });
        }} error={formErrors.doctorId} searchable placeholder="Seleccionar doctor..." data-id="element-232" />
          </div>}
      </FormSection>
      <FormSection title="Servicios" data-id="element-233">
        <div className="space-y-3" data-id="element-234">
          <div className="relative" data-id="element-235">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-236" />
            <input type="text" placeholder="Buscar servicios..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-237" />
          </div>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-2 bg-gray-50 dark:bg-gray-900/50" data-id="element-238">
            {filteredFormServices.length === 0 ? <p className="text-sm text-gray-500 italic p-2" data-id="element-239">{serviceSearchTerm ? 'No se encontraron servicios con ese nombre' : 'No hay servicios activos disponibles'}</p> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-240">
                {filteredFormServices.map(service => <label key={service.id} className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${formData.serviceIds.includes(service.id) ? 'bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}`} data-id="element-241">
                    <input type="checkbox" checked={formData.serviceIds.includes(service.id)} onChange={() => toggleService(service.id)} className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500" data-id="element-242" />
                    <div className="flex-1 min-w-0" data-id="element-243">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-244">{service.name}</span>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-245">{service.duration} min</p>
                    </div>
                  </label>)}
              </div>}
          </div>
          {formErrors.serviceId && <p className="text-sm text-red-500" data-id="element-246">{formErrors.serviceId}</p>}
          {formData.serviceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-247">
              {selectedServicesList.map(s => <span key={s.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200" data-id="element-248">
                  {s.name}
                  <button type="button" onClick={() => toggleService(s.id)} className="ml-1 hover:text-teal-900 dark:hover:text-teal-100" data-id="element-249"><XIcon className="w-3 h-3" data-id="element-250" /></button>
                </span>)}
            </div>}
          <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-251">
            <ClockIcon className="w-4 h-4 text-gray-500" data-id="element-252" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-253">Duración Total: {totalDuration} min</span>
          </div>
        </div>
      </FormSection>
      <FormSection title="Fecha y Hora" data-id="element-254">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-255">
          <Input type="date" label="Fecha" value={formData.date} onChange={e => setFormData({
          ...formData,
          date: e.target.value
        })} error={formErrors.date} data-id="element-256" />
          <Input type="time" label="Hora" value={formData.time} onChange={e => setFormData({
          ...formData,
          time: e.target.value
        })} error={formErrors.time} data-id="element-257" />
        </div>
        <button type="button" onClick={() => {
        setOverrideRestrictions(v => !v);
        setFormErrors({});
      }} className={`mt-3 w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${overrideRestrictions ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600/60' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-258">
          <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-md border-2 flex-shrink-0 transition-colors ${overrideRestrictions ? 'bg-amber-500 border-amber-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-259">
            {overrideRestrictions && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-260" />}
          </span>
          <span className="min-w-0" data-id="element-261">
            <span className="block text-sm font-semibold text-gray-900 dark:text-white" data-id="element-262">Agendar sin restricciones</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-263">Ignora el horario del doctor y permite citas que se solapan o caen dentro del margen entre citas. Úsalo solo cuando necesites forzar una cita.</span>
          </span>
        </button>
        {overrideRestrictions && <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400" data-id="element-264"><ClockIcon className="w-3.5 h-3.5 flex-shrink-0" data-id="element-265" />Validaciones de horario y disponibilidad desactivadas para esta cita.</p>}
      </FormSection>
      <FormSection title="Notas" data-id="element-266">
        <Textarea label="Notas Adicionales" value={formData.notes} onChange={e => setFormData({
        ...formData,
        notes: e.target.value
      })} rows={3} placeholder="Detalles adicionales sobre la cita..." data-id="element-267" />
      </FormSection>
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-800 modal-actions" data-id="element-268">
        <Button variant="outline" type="button" onClick={() => isEdit ? setIsEditOpen(false) : setIsFormOpen(false)} data-id="element-269">Cancelar</Button>
        <Button type="submit" data-id="element-270">{isEdit ? 'Guardar Cambios' : 'Crear Cita'}</Button>
      </div>
    </div>;
  useEffect(() => {
    if (initialDateFilter) setFilterDate(initialDateFilter);
  }, [initialDateFilter]);

  // ─── Status maps ─────────────────────────────────────────────────────────
  const accentMap: Record<string, string> = {
    programada: '#0d9488',
    completada: '#059669',
    cancelada: '#e11d48',
    reprogramada: '#d97706'
  };
  const statusLabelMap: Record<string, string> = {
    programada: 'Programada',
    completada: 'Completada',
    cancelada: 'Cancelada',
    reprogramada: 'Reprogramada'
  };
  return <div className="space-y-6" data-id="element-271">

      {/* ══════════════════════════════════════════════════════
         HEADER — tono frío teal profesional
       ══════════════════════════════════════════════════════ */}
      <div className="relative overflow-hidden rounded-2xl shadow-lg" style={{
      background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 45%, #0891b2 100%)'
    }} data-id="element-272">
        
        {/* Glow blobs */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)'
      }} data-id="element-273" />
        <div className="absolute bottom-0 left-1/3 w-64 h-64 rounded-full blur-3xl translate-y-1/2 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(8,145,178,0.3) 0%, transparent 70%)'
      }} data-id="element-274" />
        {/* Subtle grid texture */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 39px,rgba(255,255,255,1) 39px,rgba(255,255,255,1) 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,rgba(255,255,255,1) 39px,rgba(255,255,255,1) 40px)'
      }} data-id="element-275" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-white/20" data-id="element-276" />

        <div className="relative z-10 p-6 pl-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-277">
          <div data-id="element-278">
            <div className="flex items-center gap-3 mb-1.5" data-id="element-279">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-280">
                <CalendarIcon className="w-6 h-6 text-white" data-id="element-281" />
              </div>
              <h1 className="text-2xl font-bold text-white tracking-tight" data-id="element-282">Citas</h1>
            </div>
            <p className="text-teal-100/80 font-medium text-sm" data-id="element-283">
              {isOwnOnly && linkedDoctor ? `Citas de ${getDoctorPrefix(linkedDoctor.gender, getSpecialty(linkedDoctor.specialtyId)?.name)} ${linkedDoctor.name}` : 'Gestión de citas médicas'}
              {isReadOnlyAppointments && <span className="ml-2 px-2 py-0.5 rounded text-xs font-medium bg-white/15 text-white/90" data-id="element-284">Solo lectura</span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-285">
            {canExportPdf && <Button variant="outline" leftIcon={<ClockIcon className="w-4 h-4" data-id="element-287" />} onClick={() => setIsPdfExportModalOpen(true)} style={{
            background: 'rgba(255,255,255,0.12)',
            borderColor: 'rgba(255,255,255,0.25)',
            color: 'white',
            backdropFilter: 'blur(8px)'
          }} data-id="element-286">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-289" />} onClick={() => setIsExportModalOpen(true)} style={{
            background: 'rgba(255,255,255,0.12)',
            borderColor: 'rgba(255,255,255,0.25)',
            color: 'white',
            backdropFilter: 'blur(8px)'
          }} data-id="element-288">
                Exportar Excel
              </Button>}
            {canCreateAppointments && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-291" />} onClick={openNewForm} style={{
            background: 'white',
            color: '#0f766e'
          }} className="font-semibold shadow-lg shadow-black/20 hover:shadow-xl transition-shadow" data-id="element-290">
                Nueva Cita
              </Button>}
          </div>
        </div>
      </div>

      {isReadOnlyAppointments && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-292">
          <LockIcon className="w-4 h-4" data-id="element-293" />
          <span data-id="element-294">Solo tienes permisos de lectura. No puedes crear, editar o cancelar citas.</span>
        </div>}

      {hasOwnButNoDoctor && <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800" data-id="element-295">
          <div className="flex items-center gap-3 p-1" data-id="element-296">
            <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0" data-id="element-297">
              <ClockIcon className="w-5 h-5 text-white" data-id="element-298" />
            </div>
            <div data-id="element-299">
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-300">Doctor no vinculado</h3>
              <p className="text-sm text-amber-700 dark:text-amber-400" data-id="element-301">Tu cuenta ({user?.email}) no está vinculada a ningún registro de doctor. Contacta al administrador.</p>
            </div>
          </div>
        </Card>}

      {/* ══════════════════════════════════════════════════════
         FILTROS — card blanca, separada del header
       ══════════════════════════════════════════════════════ */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm p-4" data-id="element-302">
        <div className="flex flex-col gap-4" data-id="element-303">
          {/* View tabs + search row */}
          <div className="flex flex-col gap-4" data-id="element-304">
            <div className="flex flex-wrap gap-1.5" data-id="element-305">
              {viewTabs.map(tab => <button key={tab.id} onClick={() => setActiveView(tab.id as 'list' | 'calendar')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeView === tab.id ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`} data-id="element-306">
                
                  {tab.icon}{tab.label}
                </button>)}
            </div>
            <div className="flex flex-col sm:flex-row gap-3" data-id="element-307">
              <div className="flex-1 relative" data-id="element-308">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-309" />
                <Input style={{
                paddingLeft: '2.5rem'
              }} placeholder="Buscar por paciente o doctor..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-310" />
              </div>
              <div className="w-full sm:w-48" data-id="element-311">
                <Select options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="Estado" data-id="element-312" />
              </div>
            </div>
          </div>

          {/* Date filters row */}
          <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100 dark:border-gray-800" data-id="element-313">
            <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest mr-1" data-id="element-314">Fecha:</span>
            {[{
            label: 'Todas',
            value: ''
          }, {
            label: 'Hoy',
            value: today,
            icon: true
          }, {
            label: 'Mañana',
            value: getTomorrow()
          }].map(opt => {
            const isActive = filterDate === opt.value && (opt.value !== '' || !filterDate);
            const isAllActive = opt.value === '' && !filterDate;
            return <button key={opt.label} onClick={() => setFilterDate(opt.value)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${isAllActive || opt.value !== '' && filterDate === opt.value ? 'bg-teal-600 text-white shadow-sm shadow-teal-600/25' : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-315">
                  
                  {opt.icon && <CalendarIcon className="w-3.5 h-3.5" data-id="element-316" />}
                  {opt.label}
                </button>;
          })}

            {/* Custom date picker */}
            <div className="relative" ref={pickerRef} data-id="element-317">
              <button ref={pickerBtnRef} type="button" onClick={() => {
              setPickerNavDate(filterDate ? new Date(filterDate + 'T12:00:00') : new Date());
              setIsDatePickerOpen(v => !v);
            }} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${filterDate && filterDate !== today && filterDate !== getTomorrow() ? 'bg-teal-600 text-white shadow-sm shadow-teal-600/25' : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`} data-id="element-318">
                
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-319" />
                {filterDate && filterDate !== today && filterDate !== getTomorrow() ? (() => {
                const [y, m, d] = filterDate.split('-');
                return `${d}/${m}/${y}`;
              })() : 'Elegir fecha'}
              </button>

              {isDatePickerOpen && ReactDOM.createPortal(<>
                  <div className="fixed inset-0 z-[299] sm:bg-transparent bg-black/50" onClick={() => setIsDatePickerOpen(false)} data-id="element-320" />
                  <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[300] sm:translate-x-0 sm:translate-y-0" style={window.innerWidth >= 640 && pickerBtnRef.current ? {
                top: pickerBtnRef.current.getBoundingClientRect().bottom + 8,
                left: pickerBtnRef.current.getBoundingClientRect().left,
                transform: 'none'
              } : {}} data-id="element-321">
                    
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-4 w-72" data-id="element-322">
                      {(() => {
                    const pYear = pickerNavDate.getFullYear();
                    const pMonth = pickerNavDate.getMonth();
                    const pDays = getDaysInMonth(pYear, pMonth);
                    const pFirstDay = getFirstDayOfMonth(pYear, pMonth);
                    const pCells: (number | null)[] = [];
                    for (let i = 0; i < pFirstDay; i++) pCells.push(null);
                    for (let i = 1; i <= pDays; i++) pCells.push(i);
                    return <>
                            <div className="flex items-center justify-between mb-3" data-id="element-323">
                              <button type="button" onClick={() => setPickerNavDate(new Date(pYear, pMonth - 1, 1))} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-324"><ChevronLeftIcon className="w-4 h-4 text-gray-500" data-id="element-325" /></button>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-326">{getMonthName(pMonth)} {pYear}</span>
                              <button type="button" onClick={() => setPickerNavDate(new Date(pYear, pMonth + 1, 1))} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-327"><ChevronRightIcon className="w-4 h-4 text-gray-500" data-id="element-328" /></button>
                            </div>
                            <div className="grid grid-cols-7 mb-1" data-id="element-329">
                              {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map(d => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1" data-id="element-330">{d}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-0.5" data-id="element-331">
                              {pCells.map((day, idx) => {
                          if (day === null) return <div key={`e-${idx}`} data-id="element-332" />;
                          const dateStr = `${pYear}-${String(pMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const isToday = dateStr === today;
                          const isSelected = dateStr === filterDate;
                          return <button key={day} type="button" onClick={() => {
                            setFilterDate(dateStr);
                            setIsDatePickerOpen(false);
                          }} className={`aspect-square rounded-lg text-xs font-medium flex items-center justify-center transition-colors
                                      ${isSelected ? 'bg-teal-600 text-white' : ''}
                                      ${isToday && !isSelected ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 font-bold' : ''}
                                      ${!isSelected && !isToday ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-333">
                                    {day}
                                  </button>;
                        })}
                            </div>
                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700" data-id="element-334">
                              <button type="button" onClick={() => {
                          setFilterDate(today);
                          setIsDatePickerOpen(false);
                        }} className="text-xs font-medium text-teal-600 dark:text-teal-400 hover:underline" data-id="element-335">Ir a hoy</button>
                              <button type="button" onClick={() => setIsDatePickerOpen(false)} className="text-xs text-gray-400 hover:text-gray-600" data-id="element-336">Cerrar</button>
                            </div>
                          </>;
                  })()}
                    </div>
                  </div>
                </>, document.body)}
            </div>

            {filterDate && <button onClick={() => setFilterDate('')} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors" data-id="element-337">
                <XIcon className="w-3.5 h-3.5" data-id="element-338" /> Limpiar
              </button>}
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full" data-id="element-339">
              {filteredAppointments.length} cita{filteredAppointments.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
         LIST VIEW — tabla rediseñada
       ══════════════════════════════════════════════════════ */}
      {activeView === 'list' && <div className="rounded-2xl overflow-hidden border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm" data-id="element-340">

          {/* Mobile cards */}
          <div className="sm:hidden" data-id="element-341">
            {filteredAppointments.length === 0 ? <div className="py-16 flex flex-col items-center gap-3 text-gray-400 dark:text-gray-600" data-id="element-342">
                <CalendarIcon className="w-8 h-8 opacity-40" data-id="element-343" />
                <p className="text-sm font-medium" data-id="element-344">No se encontraron citas</p>
              </div> : <div className="divide-y divide-gray-50 dark:divide-gray-800/80" data-id="element-345">
                {paginatedAppointments.map(apt => {
            const patient = getPatient(apt.patientId);
            const doctor = getDoctor(apt.doctorId);
            const serviceIds = apt.serviceIds?.length > 0 ? apt.serviceIds : [apt.serviceId];
            const aptServices = services.filter(s => serviceIds.includes(s.id));
            const serviceNames = aptServices.map(s => s.name).join(', ');
            const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
            const accent = accentMap[apt.status] ?? '#9ca3af';
            return <div key={apt.id} className="relative flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 transition-colors" data-id="element-346">
                      <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full" style={{
                background: accent
              }} data-id="element-347" />
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0 ml-2" style={{
                background: `linear-gradient(135deg, ${accent}cc, ${accent})`
              }} data-id="element-348">
                        {(patient?.name?.charAt(0) || 'P').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-349">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug truncate" data-id="element-350">{patient?.name || '—'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-px" data-id="element-351">{doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '—'}</p>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-352">
                          <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400" data-id="element-353">{apt.date} · {formatTime(apt.time)}</span>
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                    background: `${accent}18`,
                    color: accent
                  }} data-id="element-354">{statusLabelMap[apt.status] ?? apt.status}</span>
                        </div>
                        {serviceNames && <p className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5" data-id="element-355">{serviceNames}</p>}
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0" data-id="element-356">
                          <button onClick={() => openDetailModal(apt)} className="p-2 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Ver detalle" data-id="element-357"><EyeIcon className="w-3.5 h-3.5" data-id="element-358" /></button>
                          {canModify && <>
                            {!isReadOnlyAppointments && <button onClick={() => openEditModal(apt)} className="p-2 rounded-lg text-gray-300 dark:text-gray-600 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all" title="Editar" data-id="element-359"><EditIcon className="w-3.5 h-3.5" data-id="element-360" /></button>}
                            <button onClick={() => openActionSelectModal(apt)} className="p-2 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Gestionar" data-id="element-361"><CheckIcon className="w-3.5 h-3.5" data-id="element-362" /></button>
                          </>}
                        </div>
                    </div>;
          })}
              </div>}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto" data-id="element-363">
            <table className="w-full border-collapse" data-id="element-364">
              <thead data-id="element-365">
                <tr className="border-b border-gray-100 dark:border-gray-800" data-id="element-366">
                  <SortableHeader label="Paciente" sortKey="patientId" currentDirection={aptIsSorted('patientId')} onSort={aptRequestSort} className="py-3.5 px-5 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-367" />
                  <SortableHeader label="Doctor" sortKey="doctorId" currentDirection={aptIsSorted('doctorId')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-368" />
                  <SortableHeader label="Servicios" sortKey="serviceId" currentDirection={aptIsSorted('serviceId')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hidden md:table-cell" data-id="element-369" />
                  <SortableHeader label="Fecha y Hora" sortKey="date" currentDirection={aptIsSorted('date')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-370" />
                  <SortableHeader label="Estado" sortKey="status" currentDirection={aptIsSorted('status')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-371" />
                  <SortableHeader label="Creada por" sortKey="createdByUserName" currentDirection={aptIsSorted('createdByUserName')} onSort={aptRequestSort} className="py-3.5 px-4 text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest hidden md:table-cell" data-id="element-372" />
                  <th className="py-3.5 px-5 text-right text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest" data-id="element-373">Acciones</th>
                </tr>
              </thead>
              <tbody data-id="element-374">
                {filteredAppointments.length === 0 ? <tr data-id="element-375">
                    <td colSpan={7} className="py-16" data-id="element-376">
                      <div className="flex flex-col items-center gap-3 text-gray-300 dark:text-gray-600" data-id="element-377">
                        <CalendarIcon className="w-8 h-8" data-id="element-378" />
                        <p className="text-sm font-medium text-gray-400 dark:text-gray-500" data-id="element-379">No se encontraron citas</p>
                      </div>
                    </td>
                  </tr> : paginatedAppointments.map(apt => {
              const patient = getPatient(apt.patientId);
              const doctor = getDoctor(apt.doctorId);
              const serviceIds = apt.serviceIds?.length > 0 ? apt.serviceIds : [apt.serviceId];
              const aptServices = services.filter(s => serviceIds.includes(s.id));
              const serviceNames = aptServices.map(s => s.name).join(', ');
              const canModify = apt.status === 'programada' || apt.status === 'reprogramada';
              const accent = accentMap[apt.status] ?? '#9ca3af';
              return <tr key={apt.id} className="group border-b border-gray-50 dark:border-gray-800/60 last:border-0 hover:bg-teal-50/30 dark:hover:bg-teal-900/10 transition-colors" data-id="element-380">
                        <td className="py-3.5 px-5" data-id="element-381">
                          <div className="flex items-center gap-3" data-id="element-382">
                            <div className="relative flex-shrink-0" data-id="element-383">
                              <div className="absolute -left-3 top-0 bottom-0 w-[3px] rounded-r-full opacity-0 group-hover:opacity-100 transition-opacity" style={{
                        background: accent
                      }} data-id="element-384" />
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[13px] font-bold text-white" style={{
                        background: `linear-gradient(135deg, ${accent}99, ${accent})`
                      }} data-id="element-385">
                                {(patient?.name?.charAt(0) || 'P').toUpperCase()}
                              </div>
                            </div>
                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100" data-id="element-386">{patient?.name || '—'}</span>
                          </div>
                        </td>
                        <td className="py-3.5 px-4" data-id="element-387">
                          <p className="text-sm text-gray-700 dark:text-gray-200 font-medium leading-snug" data-id="element-388">{doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '—'}</p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5" data-id="element-389">{doctor && getSpecialty(doctor.specialtyId)?.name}</p>
                        </td>
                        <td className="py-3.5 px-4 hidden md:table-cell" data-id="element-390">
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-1" data-id="element-391">{serviceNames || '—'}</p>
                          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium" data-id="element-392">{apt.duration} min</span>
                        </td>
                        <td className="py-3.5 px-4" data-id="element-393">
                          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 tabular-nums" data-id="element-394">{apt.date}</p>
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 font-medium mt-0.5 tabular-nums flex items-center gap-1" data-id="element-395">
                            <ClockIcon className="w-3 h-3" data-id="element-396" />{formatTime(apt.time)}
                          </p>
                        </td>
                        <td className="py-3.5 px-4" data-id="element-397">
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{
                    background: `${accent}15`,
                    color: accent
                  }} data-id="element-398">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                      background: accent
                    }} data-id="element-399" />
                            {statusLabelMap[apt.status] ?? apt.status}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 hidden md:table-cell" data-id="element-400">
                          {apt.createdByUserName ? <div className="flex items-center gap-1.5" data-id="element-401">
                              <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-402">
                                <UserIcon className="w-3 h-3 text-gray-400" data-id="element-403" />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-404">{apt.createdByUserName}</span>
                            </div> : <span className="text-gray-200 dark:text-gray-700" data-id="element-405">—</span>}
                        </td>
                        <td className="py-3.5 px-5" data-id="element-406">
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-407">
                            <button onClick={() => openDetailModal(apt)} className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Ver detalle" data-id="element-408"><EyeIcon className="w-3.5 h-3.5" data-id="element-409" /></button>
                            {canModify && <>
                                {!isReadOnlyAppointments && <button onClick={() => openEditModal(apt)} className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all" title="Editar" data-id="element-410"><EditIcon className="w-3.5 h-3.5" data-id="element-411" /></button>}
                                <button onClick={() => openActionSelectModal(apt)} className="p-1.5 rounded-lg text-gray-300 dark:text-gray-600 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all" title="Gestionar cita" data-id="element-412"><CheckIcon className="w-3.5 h-3.5" data-id="element-413" /></button>
                              </>}
                          </div>
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-gray-50 dark:border-gray-800" data-id="element-414">
            <Pagination currentPage={aptPage} totalPages={aptTotalPages} totalItems={aptTotalItems} startIndex={aptStartIndex} endIndex={aptEndIndex} onPageChange={aptGoToPage} onNext={aptNextPage} onPrev={aptPrevPage} data-id="element-415" />
          </div>
        </div>}

      {/* ── Calendar View ── */}
      {activeView === 'calendar' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-416">
          <Card className="lg:col-span-2" data-id="element-417">
            <CardHeader data-id="element-418">
              <div className="flex items-center justify-between" data-id="element-419">
                <CardTitle data-id="element-420">Calendario de Citas</CardTitle>
                <div className="flex items-center gap-2" data-id="element-421">
                  <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-422"><ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-423" /></button>
                  <span className="text-base sm:text-lg font-medium text-gray-900 dark:text-white min-w-[120px] sm:min-w-[160px] text-center" data-id="element-424">{getMonthName(month)} {year}</span>
                  <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-id="element-425"><ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-426" /></button>
                </div>
              </div>
            </CardHeader>
            <CardContent data-id="element-427">
              <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-428">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 py-2" data-id="element-429">{day}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1" data-id="element-430">
                {calendarDays.map((day, index) => {
              if (day === null) return <div key={`empty-${index}`} className="aspect-square" data-id="element-431" />;
              const dateStr = getDateString(day);
              const appointmentCount = getAppointmentCountForDay(day);
              const isToday = dateStr === today;
              const isSelected = dateStr === selectedDate;
              return <button key={day} onClick={() => setSelectedDate(dateStr)} style={isToday ? {
                background: 'linear-gradient(135deg, #0f766e, #0891b2)'
              } : undefined} className={`aspect-square rounded-lg text-sm p-1 flex flex-col items-center justify-center transition-colors
                        ${isToday ? 'text-white shadow-md' : ''}
                        ${isSelected && !isToday ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 ring-2 ring-teal-500' : ''}
                        ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-432">
                    
                      <span className="font-medium" data-id="element-433">{day}</span>
                      {appointmentCount > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full mt-0.5 ${isToday ? 'bg-white/30 text-white' : 'bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-400'}`} data-id="element-434">{appointmentCount}</span>}
                    </button>;
            })}
              </div>
            </CardContent>
          </Card>
          <Card data-id="element-435">
            <CardHeader data-id="element-436">
              <CardTitle data-id="element-437">{selectedDate === today ? 'Citas de Hoy' : `Citas del ${selectedDate}`}</CardTitle>
            </CardHeader>
            <CardContent data-id="element-438">
              {(() => {
            const visibleDateAppointments = selectedDateAppointments.filter(apt => {
              if (isOwnOnly && linkedDoctor) return apt.doctorId === linkedDoctor.id;
              return true;
            });
            return visibleDateAppointments.length === 0 ? <p className="text-center text-gray-500 dark:text-gray-400 py-8" data-id="element-439">No hay citas programadas</p> : <div className="space-y-3" data-id="element-440">
                    {visibleDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const serviceIds = apt.serviceIds?.length > 0 ? apt.serviceIds : [apt.serviceId];
                const aptServices = services.filter(s => serviceIds.includes(s.id));
                const serviceNames = aptServices.map(s => s.name).join(', ');
                const calAccent = accentMap[apt.status] ?? '#9ca3af';
                return <div key={apt.id} onClick={() => openDetailModal(apt)} className="p-3.5 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow relative cursor-pointer" style={{
                  borderLeft: `3px solid ${calAccent}`
                }} data-id="element-441">
                          <div className="flex items-start justify-between mb-2.5" data-id="element-442">
                            <div className="flex items-center gap-2" data-id="element-443">
                              <div className="p-1.5 rounded-lg" style={{
                        background: `${calAccent}15`
                      }} data-id="element-444">
                                <ClockIcon className="w-3.5 h-3.5" style={{
                          color: calAccent
                        }} data-id="element-445" />
                              </div>
                              <div data-id="element-446">
                                <p className="font-bold text-gray-900 dark:text-white text-sm" data-id="element-447">{formatTime(apt.time)}</p>
                                <p className="text-xs text-gray-400 dark:text-gray-500" data-id="element-448">{apt.duration} min</p>
                              </div>
                            </div>
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{
                      background: `${calAccent}15`,
                      color: calAccent
                    }} data-id="element-449">
                              <span className="w-1.5 h-1.5 rounded-full" style={{
                        background: calAccent
                      }} data-id="element-450" />
                              {statusLabelMap[apt.status] ?? apt.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1" data-id="element-451">
                            <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{
                      background: 'linear-gradient(135deg, #0f766e, #0891b2)'
                    }} data-id="element-452">
                              {(patient?.name?.charAt(0) || 'P').toUpperCase()}
                            </div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" data-id="element-453">{patient?.name}</p>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 pl-8" data-id="element-454">{doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : ''}</p>
                          {serviceNames && <p className="text-xs text-teal-600 dark:text-teal-400 mt-1.5 pl-8 truncate font-medium" data-id="element-455">{serviceNames}</p>}
                        </div>;
              })}
                  </div>;
          })()}
            </CardContent>
          </Card>
        </div>}

      {/* ── Modals ── */}
      <Modal isOpen={isDetailOpen} onClose={() => setIsDetailOpen(false)} title="Detalle de la Cita" size="md" icon={<EyeIcon className="w-6 h-6" data-id="element-457" />} accentColor="sky" data-id="element-456">
        {selectedAppointment && (() => {
        const pt = getPatient(selectedAppointment.patientId);
        const doc = getDoctor(selectedAppointment.doctorId);
        const spec = doc ? getSpecialty(doc.specialtyId) : null;
        const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
        const detailServices = services.filter(s => sIds.includes(s.id));
        const detailTotalPrice = detailServices.reduce((sum, s) => sum + (s.price || 0), 0);
        const detailAccent = accentMap[selectedAppointment.status] ?? '#9ca3af';
        return <div className="space-y-5" data-id="element-458">
            {/* Header card */}
            <div className="flex items-center gap-4 p-4 rounded-2xl" style={{
            background: `${detailAccent}12`
          }} data-id="element-459">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0" style={{
              background: `linear-gradient(135deg, ${detailAccent}cc, ${detailAccent})`
            }} data-id="element-460">
                {(pt?.name?.charAt(0) || 'P').toUpperCase()}
              </div>
              <div className="min-w-0 flex-1" data-id="element-461">
                <p className="text-lg font-bold text-gray-900 dark:text-white truncate" data-id="element-462">{pt?.name || '—'}</p>
                <div className="flex items-center gap-2 mt-1" data-id="element-463">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full" style={{
                  background: `${detailAccent}20`,
                  color: detailAccent
                }} data-id="element-464">
                    <span className="w-1.5 h-1.5 rounded-full" style={{
                    background: detailAccent
                  }} data-id="element-465" />
                    {statusLabelMap[selectedAppointment.status] ?? selectedAppointment.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-466">
              <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-467">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-468"><CalendarIcon className="w-3.5 h-3.5" data-id="element-469" />Fecha</div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums" data-id="element-470">{selectedAppointment.date}</p>
              </div>
              <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-471">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-472"><ClockIcon className="w-3.5 h-3.5" data-id="element-473" />Hora · Duración</div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums" data-id="element-474">{formatTime(selectedAppointment.time)} · {selectedAppointment.duration} min</p>
              </div>
              <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-475">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-476"><StethoscopeIcon className="w-3.5 h-3.5" data-id="element-477" />Doctor</div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-478">{doc ? `${getDoctorPrefix(doc.gender, spec?.name)} ${doc.name}` : '—'}</p>
                {spec?.name && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5" data-id="element-479">{spec.name}</p>}
              </div>
              <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-480">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-481"><UserIcon className="w-3.5 h-3.5" data-id="element-482" />Registrado por</div>
                <p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-483">{selectedAppointment.createdByUserName || '—'}</p>
              </div>
            </div>

            {/* Services */}
            <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-484">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2.5" data-id="element-485"><FileTextIcon className="w-3.5 h-3.5" data-id="element-486" />Servicios</div>
              {detailServices.length > 0 ? <div className="space-y-2" data-id="element-487">
                  {detailServices.map(s => <div key={s.id} className="flex items-center justify-between gap-3" data-id="element-488">
                      <span className="text-sm text-gray-700 dark:text-gray-200" data-id="element-489">{s.name}</span>
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0" data-id="element-490">{formatCurrency(s.price || 0)}</span>
                    </div>)}
                  <div className="flex items-center justify-between gap-3 pt-2 mt-1 border-t border-gray-200 dark:border-gray-600" data-id="element-491">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-492">Total</span>
                    <span className="text-sm font-bold text-teal-600 dark:text-teal-400 tabular-nums" data-id="element-493">{formatCurrency(detailTotalPrice)}</span>
                  </div>
                </div> : <p className="text-sm text-gray-400 dark:text-gray-500" data-id="element-494">Sin servicios</p>}
            </div>

            {/* Notes */}
            {selectedAppointment.notes && <div className="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700" data-id="element-495">
              <div className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-496">Notas</div>
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed" data-id="element-497">{selectedAppointment.notes}</p>
            </div>}

            {/* Reschedule info */}
            {(selectedAppointment.originalDate || selectedAppointment.originalTime) && <div className="p-3.5 rounded-xl bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800/40" data-id="element-498">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wide mb-1.5" data-id="element-499"><RefreshCwIcon className="w-3.5 h-3.5" data-id="element-500" />Reprogramada</div>
              <p className="text-sm text-gray-700 dark:text-gray-200 tabular-nums" data-id="element-501">Original: {selectedAppointment.originalDate || '—'}{selectedAppointment.originalTime ? ` · ${formatTime(selectedAppointment.originalTime)}` : ''}</p>
            </div>}

            {/* Cancellation reason */}
            {selectedAppointment.cancelReason && <div className="p-3.5 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40" data-id="element-502">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide mb-1.5" data-id="element-503"><XIcon className="w-3.5 h-3.5" data-id="element-504" />Motivo de cancelación</div>
              <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed" data-id="element-505">{selectedAppointment.cancelReason}</p>
            </div>}

            {/* Next appointment */}
            {selectedAppointment.nextAppointmentDate && <div className="p-3.5 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800/40" data-id="element-506">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-1.5" data-id="element-507"><CalendarIcon className="w-3.5 h-3.5" data-id="element-508" />Próxima cita</div>
              <p className="text-sm text-gray-700 dark:text-gray-200 tabular-nums" data-id="element-509">{selectedAppointment.nextAppointmentDate}</p>
              {selectedAppointment.nextAppointmentNotes && <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1 whitespace-pre-wrap" data-id="element-510">{selectedAppointment.nextAppointmentNotes}</p>}
            </div>}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-1 modal-actions" data-id="element-511">
              {!isReadOnlyAppointments && (selectedAppointment.status === 'programada' || selectedAppointment.status === 'reprogramada') && <Button variant="outline" leftIcon={<EditIcon className="w-4 h-4" data-id="element-513" />} onClick={() => {
              const apt = selectedAppointment;
              setIsDetailOpen(false);
              openEditModal(apt);
            }} data-id="element-512">Editar</Button>}
              <Button variant="secondary" onClick={() => setIsDetailOpen(false)} data-id="element-514">Cerrar</Button>
            </div>
          </div>;
      })()}
      </Modal>

      <Modal isOpen={isActionSelectOpen} onClose={() => setIsActionSelectOpen(false)} title="Gestionar Cita" size="sm" icon={<CalendarIcon className="w-6 h-6" data-id="element-516" />} accentColor="sky" data-id="element-515">
        <div className="space-y-4" data-id="element-517">
          {selectedAppointment && (() => {
          const pt = getPatient(selectedAppointment.patientId);
          const doc = getDoctor(selectedAppointment.doctorId);
          const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
          const svcNames = services.filter(s => sIds.includes(s.id)).map(s => s.name).join(', ');
          return <div className="flex items-center gap-3 p-3.5 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-518">
                <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center flex-shrink-0" data-id="element-519"><UserIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-520" /></div>
                <div className="min-w-0 flex-1" data-id="element-521">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-id="element-522">{pt?.name || '-'}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-523">{doc ? `${getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)} ${doc.name}` : '-'}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5" data-id="element-524">{selectedAppointment.date} · {formatTime(selectedAppointment.time)}{svcNames ? ` · ${svcNames}` : ''}</p>
                </div>
              </div>;
        })()}
          <p className="text-sm text-center text-gray-500 dark:text-gray-400 font-medium" data-id="element-525">¿Qué deseas hacer con esta cita?</p>
          <div className="space-y-2.5" data-id="element-526">
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openCompleteModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-green-400 dark:hover:border-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-all group text-left" data-id="element-527">
              <div className="w-11 h-11 rounded-xl bg-green-100 dark:bg-green-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors" data-id="element-528"><CheckIcon className="w-5 h-5 text-green-700 dark:text-green-400" data-id="element-529" /></div>
              <div className="flex-1 min-w-0" data-id="element-530"><p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-531">Completar cita</p><p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-532">Marcar como completada y programar próxima cita</p></div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-green-500 transition-colors flex-shrink-0" data-id="element-533" />
            </button>
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openRescheduleModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-teal-400 dark:hover:border-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-all group text-left" data-id="element-534">
              <div className="w-11 h-11 rounded-xl bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors" data-id="element-535"><RefreshCwIcon className="w-5 h-5 text-teal-700 dark:text-teal-400" data-id="element-536" /></div>
              <div className="flex-1 min-w-0" data-id="element-537"><p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-538">Reprogramar cita</p><p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-539">Cambiar la fecha y hora de la cita</p></div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-teal-500 transition-colors flex-shrink-0" data-id="element-540" />
            </button>
            <button type="button" onClick={() => {
            setIsActionSelectOpen(false);
            if (selectedAppointment) openCancelModal(selectedAppointment);
          }} className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-transparent bg-gray-50 dark:bg-gray-700/40 hover:border-red-400 dark:hover:border-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all group text-left" data-id="element-541">
              <div className="w-11 h-11 rounded-xl bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-200 transition-colors" data-id="element-542"><XIcon className="w-5 h-5 text-red-700 dark:text-red-400" data-id="element-543" /></div>
              <div className="flex-1 min-w-0" data-id="element-544"><p className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-545">Cancelar cita</p><p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-546">Cancelar y notificar al paciente y al doctor</p></div>
              <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-red-500 transition-colors flex-shrink-0" data-id="element-547" />
            </button>
          </div>
          <button type="button" onClick={() => setIsActionSelectOpen(false)} className="w-full py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all" data-id="element-548">Volver</button>
        </div>
      </Modal>

      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nueva Cita" size="lg" icon={<CalendarIcon className="w-6 h-6" data-id="element-550" />} accentColor="sky" data-id="element-549">
        <form onSubmit={handleSubmit} data-id="element-551">{renderAppointmentForm(false)}</form>
      </Modal>
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Editar Cita" size="lg" icon={<EditIcon className="w-6 h-6" data-id="element-553" />} accentColor="blue" data-id="element-552">
        <form onSubmit={handleEditSubmit} data-id="element-554">{renderAppointmentForm(true)}</form>
      </Modal>

      <Modal isOpen={isCancelOpen} onClose={() => setIsCancelOpen(false)} title="Cancelar Cita" size="md" data-id="element-555">
        <div className="space-y-4" data-id="element-556">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-557">¿Está seguro de que desea cancelar esta cita? Se notificará al paciente y al doctor.</p>
          <Textarea label="Motivo de cancelación" value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Ingrese el motivo de la cancelación..." rows={3} data-id="element-558" />
          <div className="flex justify-end gap-3 modal-actions" data-id="element-559">
            <Button variant="outline" onClick={() => setIsCancelOpen(false)} data-id="element-560">Volver</Button>
            <Button variant="danger" onClick={handleCancel} data-id="element-561">Cancelar Cita</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isRescheduleOpen} onClose={() => setIsRescheduleOpen(false)} title="Reprogramar Cita" size="md" data-id="element-562">
        <div className="space-y-4" data-id="element-563">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-564">Seleccione la nueva fecha y hora para la cita.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-565">
            <Input type="date" label="Nueva Fecha" value={rescheduleData.date} onChange={e => setRescheduleData({
            ...rescheduleData,
            date: e.target.value
          })} data-id="element-566" />
            <Input type="time" label="Nueva Hora" value={rescheduleData.time} onChange={e => setRescheduleData({
            ...rescheduleData,
            time: e.target.value
          })} data-id="element-567" />
          </div>
          <button type="button" onClick={() => setOverrideRestrictions(v => !v)} className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${overrideRestrictions ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-600/60' : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-568">
            <span className={`mt-0.5 flex items-center justify-center w-5 h-5 rounded-md border-2 flex-shrink-0 transition-colors ${overrideRestrictions ? 'bg-amber-500 border-amber-500' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-569">
              {overrideRestrictions && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-570" />}
            </span>
            <span className="min-w-0" data-id="element-571">
              <span className="block text-sm font-semibold text-gray-900 dark:text-white" data-id="element-572">Reprogramar sin restricciones</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-573">Permite solapar con otra cita o ignorar el margen entre citas.</span>
            </span>
          </button>
          <div className="flex justify-end gap-3 modal-actions" data-id="element-574">
            <Button variant="outline" onClick={() => setIsRescheduleOpen(false)} data-id="element-575">Cancelar</Button>
            <Button onClick={handleReschedule} data-id="element-576">Reprogramar</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isCompleteOpen} onClose={() => setIsCompleteOpen(false)} title="Completar Cita" size="lg" data-id="element-577">
        <div className="space-y-4" data-id="element-578">
          {selectedAppointment && <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4" data-id="element-579">
              <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-580">
                <div data-id="element-581"><span className="text-gray-500 dark:text-gray-400" data-id="element-582">Paciente:</span><p className="font-medium text-gray-900 dark:text-white" data-id="element-583">{getPatient(selectedAppointment.patientId)?.name}</p></div>
                <div data-id="element-584"><span className="text-gray-500 dark:text-gray-400" data-id="element-585">Doctor:</span><p className="font-medium text-gray-900 dark:text-white" data-id="element-586">{(() => {
                  const doc = getDoctor(selectedAppointment.doctorId);
                  return doc ? `${getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)} ${doc.name}` : '-';
                })()}</p></div>
                <div data-id="element-587"><span className="text-gray-500 dark:text-gray-400" data-id="element-588">Fecha:</span><p className="font-medium text-gray-900 dark:text-white" data-id="element-589">{selectedAppointment.date} - {formatTime(selectedAppointment.time)}</p></div>
                <div data-id="element-590"><span className="text-gray-500 dark:text-gray-400" data-id="element-591">Servicios:</span><p className="font-medium text-gray-900 dark:text-white" data-id="element-592">{(() => {
                  const sIds = selectedAppointment.serviceIds?.length > 0 ? selectedAppointment.serviceIds : [selectedAppointment.serviceId];
                  return services.filter(s => sIds.includes(s.id)).map(s => s.name).join(', ') || '-';
                })()}</p></div>
                {selectedAppointment.createdByUserName && <div className="col-span-2" data-id="element-593"><span className="text-gray-500 dark:text-gray-400" data-id="element-594">Creada por:</span><p className="font-medium text-gray-900 dark:text-white flex items-center gap-1.5" data-id="element-595"><UserIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-596" />{selectedAppointment.createdByUserName}</p></div>}
              </div>
            </div>}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4" data-id="element-597">
            <p className="text-sm text-green-800 dark:text-green-300" data-id="element-598">¿Confirmar que esta cita fue completada exitosamente?</p>
          </div>
          <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4" data-id="element-599">
            <div className="flex items-center gap-2 mb-4" data-id="element-600">
              <CalendarIcon className="w-4 h-4 text-gray-400" data-id="element-601" />
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-602">Próxima Cita Sugerida</h4>
              <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full" data-id="element-603">Opcional</span>
            </div>
            <div className="space-y-4" data-id="element-604">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-605">
                <Input type="date" label="Fecha Sugerida" value={completeData.nextAppointmentDate} onChange={e => setCompleteData({
                ...completeData,
                nextAppointmentDate: e.target.value
              })} min={getToday()} data-id="element-606" />
                <Input type="time" label="Hora Sugerida" value={completeData.nextAppointmentTime} onChange={e => setCompleteData({
                ...completeData,
                nextAppointmentTime: e.target.value
              })} data-id="element-607" />
              </div>
              <div data-id="element-608">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-609">Servicios para la Próxima Cita</label>
                {completeData.nextServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-610">
                    {completeData.nextServiceIds.map(sId => {
                  const svc = getService(sId);
                  if (!svc) return null;
                  return <span key={sId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200" data-id="element-611">{svc.name}<button type="button" onClick={() => setCompleteData(prev => ({
                      ...prev,
                      nextServiceIds: prev.nextServiceIds.filter(id => id !== sId)
                    }))} className="ml-1 hover:text-teal-900" data-id="element-612"><XIcon className="w-3 h-3" data-id="element-613" /></button></span>;
                })}
                  </div>}
                <div className="relative mb-2" data-id="element-614">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-615" />
                  <input type="text" placeholder="Buscar servicios para agregar..." value={serviceSearchTerm} onChange={e => setServiceSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-616" />
                </div>
                <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-617">
                  {availableServices.filter(s => serviceSearchTerm ? s.name.toLowerCase().includes(serviceSearchTerm.toLowerCase()) : true).map(service => {
                  const isSelected = completeData.nextServiceIds.includes(service.id);
                  return <label key={service.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-teal-50 dark:bg-teal-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-618"><input type="checkbox" checked={isSelected} onChange={() => setCompleteData(prev => ({
                      ...prev,
                      nextServiceIds: isSelected ? prev.nextServiceIds.filter(id => id !== service.id) : [...prev.nextServiceIds, service.id]
                    }))} className="w-4 h-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500" data-id="element-619" /><div className="flex-1 min-w-0" data-id="element-620"><span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-621">{service.name}</span><span className="text-xs text-gray-500 dark:text-gray-400 ml-2" data-id="element-622">{service.duration} min</span></div></label>;
                })}
                </div>
              </div>
              <Textarea label="Notas para la Próxima Cita" value={completeData.nextAppointmentNotes} onChange={e => setCompleteData({
              ...completeData,
              nextAppointmentNotes: e.target.value
            })} placeholder="Ej: Control post-tratamiento, siguiente sesión..." rows={2} data-id="element-623" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 modal-actions" data-id="element-624">
            <Button variant="outline" onClick={() => setIsCompleteOpen(false)} data-id="element-625">Cancelar</Button>
            <Button onClick={handleComplete} leftIcon={<CheckIcon className="w-4 h-4" data-id="element-627" />} data-id="element-626">Completar Cita</Button>
          </div>
        </div>
      </Modal>

      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Citas" isExporting={isExporting} data-id="element-628" />
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Citas a PDF" isExporting={isPdfExporting} data-id="element-629" />

    </div>;
}